/**
 * A small, well-known seeded PRNG, so a seating can be "random" and still be
 * REPRODUCED: the same seed and the same input give the same plan, which is what
 * lets the dialog's preview and its Apply be the very same arrangement.
 *
 * mulberry32 (Tommy Ettinger, public domain): 32 bits of state, one multiply-mix
 * per draw, passes the usual smoke tests, and is ~10 lines. That is all a seating
 * needs. It is NOT cryptographic and does not try to be.
 *
 * Nothing in the planner may call `Math.random`; every unforced ordering decision
 * draws from one of these. {@link newSeed} is the one place entropy comes in, and
 * only the dialog calls it — the planner takes the seed as a parameter.
 */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface Random {
  /** A float in [0, 1). */
  next(): number;
  /** An integer in [0, n). */
  int(n: number): number;
  /** A NEW array in a uniformly random order (Fisher-Yates); the input is untouched. */
  shuffle<T>(items: readonly T[]): T[];
  /** One of the items, uniformly. Throws on an empty list: the caller decides what nothing means. */
  pick<T>(items: readonly T[]): T;
}

export function createRandom(seed: number): Random {
  const next = mulberry32(seed);
  const int = (n: number) => Math.floor(next() * n);
  return {
    next,
    int,
    shuffle<T>(items: readonly T[]): T[] {
      const out = [...items];
      for (let i = out.length - 1; i > 0; i -= 1) {
        const j = int(i + 1);
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    },
    pick<T>(items: readonly T[]): T {
      if (items.length === 0) throw new Error('pick() of an empty list');
      return items[int(items.length)];
    },
  };
}

/** A fresh 32-bit seed for the dialog's "Shuffle". Uses the browser's CSPRNG; the planner never sees this call. */
export function newSeed(): number {
  const words = new Uint32Array(1);
  crypto.getRandomValues(words);
  return words[0];
}
